import React, { useEffect, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { apiGet, formatLKR } from "@/lib/api";
import { Trash2, Sparkles } from "lucide-react";

const GOALS = [
  { value: "Dividend Income", desc: "Maximize ongoing cash flow" },
  { value: "Long Term Growth", desc: "Compounding capital appreciation" },
  { value: "Balanced", desc: "Mix of yield and growth" },
  { value: "Capital Preservation", desc: "Defensive, low drawdown" },
];
const RISKS = ["Conservative", "Moderate", "Aggressive"];
const HORIZONS = ["<1 year", "1-3 years", "3-5 years", "5+ years"];

// Phase 7 Stage 9 Part D — preview text for the goal-default sector targets.
// Backend equivalent lives in /app/backend/goal_targets.py.
const GOAL_DEFAULTS = {
  "Dividend Income":
    "Banks 30% · Diversified Financials 20% · Insurance 20% · Plantations 15% · Food Bev & Tobacco 10% · Cash 5%",
  "Long Term Growth":
    "Diversified Holdings 25% · Capital Goods 20% · Telecommunications 20% · Healthcare 15% · Materials 15% · Cash 5%",
  Balanced:
    "Banks 25% · Div Financials 15% · Insurance 15% · Div Holdings 15% · Plantations 15% · Food Bev 10% · Cash 5%",
  "Capital Preservation":
    "No fixed sector targets — follow top BUY signals only · Max single stock 25% · Cash 10%",
};

const PortfolioSetup = ({
  initial = {},
  onSubmit,
  submitting,
  allowDelete,
  onDelete,
  embedded = false,
  submitLabel,
}) => {
  const [name, setName] = useState(initial.portfolio_name || "My CSE Portfolio");
  const [amount, setAmount] = useState(initial.investment_amount || 1000000);
  const [goal, setGoal] = useState(initial.goal || "Dividend Income");
  const [risk, setRisk] = useState(initial.risk_level || "Moderate");
  const [horizon, setHorizon] = useState(initial.holding_period || "3-5 years");
  const [sectorsToAvoid, setSectorsToAvoid] = useState(initial.sectors_to_avoid || []);
  const [sectors, setSectors] = useState([]);

  useEffect(() => {
    apiGet("/sectors").then(setSectors).catch(() => {});
  }, []);

  const toggleSector = (s) => {
    setSectorsToAvoid((arr) => (arr.includes(s) ? arr.filter((x) => x !== s) : [...arr, s]));
  };

  const submit = (e) => {
    e.preventDefault();
    onSubmit({
      portfolio_name: name,
      investment_amount: Number(amount),
      goal,
      risk_level: risk,
      holding_period: horizon,
      sectors_to_avoid: sectorsToAvoid,
    });
  };

  const Inner = (
    <form onSubmit={submit} className="space-y-6" data-testid="portfolio-setup">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div className="space-y-1.5">
          <Label>Portfolio name</Label>
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            data-testid="portfolio-name-input"
            placeholder="e.g. Dividend Income, Growth Portfolio, Wife's Portfolio…"
          />
        </div>
        <div className="space-y-1.5">
          <Label>Investment amount (LKR)</Label>
          <Input
            type="number"
            min="10000"
            step="1000"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            data-testid="portfolio-amount-input"
          />
          <div className="text-xs text-muted-foreground">{formatLKR(Number(amount))}</div>
        </div>
      </div>

      <div className="space-y-2">
        <Label>Investment goal</Label>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-2" data-testid="portfolio-goal-grid">
          {GOALS.map((g) => (
            <button
              type="button"
              key={g.value}
              onClick={() => setGoal(g.value)}
              data-testid={`portfolio-goal-${g.value.replace(/\s+/g, "-")}`}
              className={`text-left rounded-lg border p-3 transition-colors duration-150 ${
                goal === g.value ? "border-primary bg-accent/40 ring-1 ring-primary/30" : "hover:bg-muted/40"
              }`}
            >
              <div className="text-sm font-medium">{g.value}</div>
              <div className="text-xs text-muted-foreground mt-0.5">{g.desc}</div>
            </button>
          ))}
        </div>
        {/* Phase 7 Stage 9 Part D — show goal-default sector targets */}
        {goal && GOAL_DEFAULTS[goal] && (
          <div
            className="rounded-md border bg-muted/20 p-2.5 text-xs space-y-1"
            data-testid="goal-default-targets"
          >
            <div className="font-display font-semibold text-foreground">
              Default sector targets for {goal}:
            </div>
            <div className="text-muted-foreground">{GOAL_DEFAULTS[goal]}</div>
            <div className="text-[11px] text-muted-foreground">
              These will be saved automatically as your initial sector targets. You can
              edit them anytime on the Health tab.
            </div>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div className="space-y-2">
          <Label>Risk level</Label>
          <ToggleGroup type="single" value={risk} onValueChange={(v) => v && setRisk(v)} className="justify-start" data-testid="portfolio-risk-toggle">
            {RISKS.map((r) => (
              <ToggleGroupItem key={r} value={r}>
                {r}
              </ToggleGroupItem>
            ))}
          </ToggleGroup>
        </div>
        <div className="space-y-2">
          <Label>Holding period</Label>
          <ToggleGroup type="single" value={horizon} onValueChange={(v) => v && setHorizon(v)} className="justify-start flex-wrap" data-testid="portfolio-horizon-toggle">
            {HORIZONS.map((r) => (
              <ToggleGroupItem key={r} value={r}>
                {r}
              </ToggleGroupItem>
            ))}
          </ToggleGroup>
        </div>
      </div>

      <div className="space-y-2">
        <Label>Sectors to avoid (optional)</Label>
        <div className="flex flex-wrap gap-2" data-testid="portfolio-avoid-sectors">
          {sectors.map((s) => {
            const on = sectorsToAvoid.includes(s);
            return (
              <button
                type="button"
                key={s}
                onClick={() => toggleSector(s)}
                className={`rounded-full border px-3 py-1 text-xs transition-colors duration-150 ${
                  on ? "bg-destructive/15 text-destructive border-destructive/40" : "hover:bg-muted/40"
                }`}
              >
                {s}
              </button>
            );
          })}
        </div>
      </div>

      <div className="flex items-center justify-between gap-2 flex-wrap pt-1">
        {allowDelete ? (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="text-destructive hover:text-destructive hover:bg-destructive/10 gap-2"
            onClick={onDelete}
            data-testid="portfolio-delete-button"
          >
            <Trash2 className="h-4 w-4" /> Delete portfolio
          </Button>
        ) : <span />}
        <Button type="submit" disabled={submitting} className="gap-2" data-testid="portfolio-submit-button">
          <Sparkles className="h-4 w-4" />
          {submitting ? "Generating with Claude…" : (submitLabel || "Get AI suggestion")}
        </Button>
      </div>
    </form>
  );

  if (embedded) return <div>{Inner}</div>;

  return (
    <Card className="border bg-card">
      <CardContent className="p-5">
        <div className="mb-4">
          <div className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary" />
            <span className="font-display font-semibold">Set up your portfolio</span>
          </div>
          <p className="text-sm text-muted-foreground mt-1">
            Tell us your investing preferences and Claude will suggest a CSE portfolio that fits.
          </p>
        </div>
        {Inner}
      </CardContent>
    </Card>
  );
};

export default PortfolioSetup;
